import React, { useState } from "react";
import { CheckCircle, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Card from "./card";
import ProgressBar from "./progressBar";
import Button from "./button";

const PASSING_SCORE = 80;

const LessonCompletePanel = ({
  lessonData = {
    accuracy: 0,
    attempts: 0,
    incorrectAnswers: [],
    allQuestions: 0,
    completedChapters: 0,
    allChapters: 0,
    progress: {
      status: "not_started",
      score: 0,
      attempts: [],
    },
  },
  lessonTitle = "Lesson",
  nextLessonTitle = "Next Lesson",
  onReviewMistakes = () => {},
  onStartNextLesson = () => {},
  onRetryChapter = null,
  onReturnToMyLessons = () => {},
  isOpen,
}) => {
  const [selectedButton, setSelectedButton] = useState(null);
  const [isNotPassedModalDismissed, setIsNotPassedModalDismissed] =
    useState(false);
  const navigate = useNavigate();

  const handleStartNextLesson = () => {
    setSelectedButton("next");
    onStartNextLesson();
    navigate("/my-lessons", { state: { refresh: true } });
  };

  // Chapter-wide values supplied by the backend. Pass/fail is decided on the cumulative
  // chapter score, never on the score of a single 25-question session.
  const passingScore = lessonData.passingScore ?? PASSING_SCORE;
  const chapterProgress = lessonData.chapterProgress || {};
  const chapterCompleted = chapterProgress.completed ?? 0;
  const chapterTotal =
    chapterProgress.total ??
    (Array.isArray(lessonData.allQuestions)
      ? lessonData.allQuestions.length
      : lessonData.allQuestions || 0);
  const chapterScore =
    lessonData.chapterScore ?? lessonData.progress?.score ?? 0;
  const isChapterComplete = Boolean(lessonData.isChapterComplete);
  const isChapterPassed = Boolean(lessonData.isChapterPassed);

  // The retry modal appears only once every required chapter question has been answered
  // and the cumulative chapter score is still below the passing mark.
  const showNotPassedModal =
    isChapterComplete && !isChapterPassed && !isNotPassedModalDismissed;

  const allChapters =
    lessonData.allChapters ||
    (Array.isArray(lessonData.chapters) ? lessonData.chapters.length : 0);
  const completedChapters = lessonData.completedChapters ?? 0;

  const incorrectCount = lessonData.incorrectAnswers?.length || 0;
  const performedCount = Array.isArray(lessonData.allQuestions)
    ? lessonData.allQuestions.length
    : lessonData.allQuestions || 0;

  const handleReviewMistakes = () => {
    setSelectedButton("review");
    setIsNotPassedModalDismissed(true);
    onReviewMistakes();
  };

  const handleRetryChapter = () => {
    setSelectedButton("retry");
    setIsNotPassedModalDismissed(true);
    if (onRetryChapter) {
      onRetryChapter();
    } else {
      handleStartNextLesson();
    }
  };

  return (
    <div className={`fixed inset-0 z-[100] ${isOpen ? "" : "hidden"}`}>
      {/* Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-30" />
      {/* Fullscreen/Sliding Panel */}
      <div
        className={`fixed inset-0 w-full h-full bg-[#fcf7ec] shadow-2xl z-[101] overflow-y-auto transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        {isChapterPassed ? (
          <div className="flex flex-col items-center text-center px-4 py-6 sm:px-8 sm:py-8 mb-6 sm:mb-8">
            <div className="bg-red-600 rounded-full p-3 mb-4">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-black mb-1">
              Lesson Complete! 🎉
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              {lessonTitle} is now completed!
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center px-4 py-6 sm:px-8 sm:py-8 mb-6 sm:mb-8">
            <div className="bg-yellow-500 rounded-full p-3 mb-4">
              <CheckCircle size={40} className="text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-black mb-1">
              Lesson Incomplete
            </h2>
            <p className="text-sm sm:text-base text-gray-600">
              Nice progress in {lessonTitle}! Keep going and continue learning
              to complete this lesson.
            </p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 sm:mb-8 px-4 sm:px-8">
          {/* Chapter Progress replaces the historical Accuracy metric on the learner-facing
              screen. Accuracy is still calculated and returned by the API for analytics. */}
          <Card className="p-4 text-center sm:text-left bg-white rounded-lg border border-gray-600">
            <div className="text-2xl sm:text-3xl font-semibold text-black mb-1">
              {chapterCompleted}/{chapterTotal}
            </div>
            <div className="text-sm sm:text-base text-gray-600">
              Chapter Progress
            </div>
          </Card>
          <Card className="p-4 text-center sm:text-left bg-white rounded-lg border border-gray-600">
            <div className="text-2xl sm:text-3xl font-semibold text-black mb-1">
              {lessonData.attempts}
            </div>
            <div className="text-sm sm:text-base text-gray-600">Attempts</div>
          </Card>
          <Card className="p-4 text-center sm:text-left bg-white rounded-lg border border-gray-600">
            <div className="text-2xl sm:text-3xl font-semibold text-black mb-1">
              {incorrectCount}/{performedCount}
            </div>
            <div className="text-sm sm:text-base text-gray-600">
              Incorrect Answers
            </div>
          </Card>
          <Card className="p-4 text-center sm:text-left bg-white rounded-lg border border-gray-600">
            <div className="text-2xl sm:text-3xl font-semibold text-black mb-1">
              {chapterScore}
            </div>
            <div className="text-sm sm:text-base text-gray-600">Score</div>
          </Card>
        </div>

        {/* Unit Progress */}
        <div className="mb-6 sm:mb-8 p-4 bg-white rounded-lg border border-gray-600 mx-4 sm:mx-8">
          <div className="text-base text-left sm:text-lg font-semibold text-black mb-2">
            Unit Progress
          </div>
          <div className="text-base text-left text-gray-600 mb-1">
            {completedChapters} of {allChapters} chapters completed
          </div>
          <ProgressBar
            value={completedChapters}
            max={allChapters}
            showLabel={false}
            variant="primary"
          />
        </div>

        {/* Action Buttons - Desktop */}
        <div className="hidden sm:grid grid-cols-2 gap-4 px-4 sm:px-8">
          {/* Review Mistakes Card */}
          {incorrectCount > 0 && (
            <Card className="p-4 flex flex-col justify-between border border-gray-600 rounded-lg">
              <div>
                <div className="text-lg text-left font-semibold text-black mb-1">
                  Review Mistakes
                </div>
                <div className="text-base text-left text-gray-600 mb-4">
                  Review the questions you got wrong to improve your knowledge.
                </div>
              </div>
              <Button
                className={`w-full border-2 border-red-600 ${
                  selectedButton === "review"
                    ? "!bg-red-600 !text-white"
                    : "!bg-white !text-red-600"
                }`}
                onClick={handleReviewMistakes}
              >
                Review Mistakes
              </Button>
            </Card>
          )}
          {/* Next Lesson Card */}
          <Card
            className={`p-4 flex flex-col justify-between border border-gray-600 rounded-lg ${
              incorrectCount === 0 ? "col-span-2" : ""
            }`}
          >
            <div>
              <div className="text-lg text-left font-semibold text-black mb-1">
                Return to Lessons
              </div>
              <div className="text-base text-left text-gray-600 mb-4">
                Return to your lessons and continue when you are ready.
              </div>
            </div>
            <Button
              className={`w-full border-2 border-red-600 ${
                selectedButton === "next"
                  ? "!bg-red-600 !text-white"
                  : "!bg-white !text-red-600"
              }`}
              onClick={handleStartNextLesson}
            >
              Back To Lessons
            </Button>
          </Card>
        </div>

        {/* Action Buttons - Mobile */}
        <div className="flex flex-col space-y-4 sm:hidden px-4 sm:px-8">
          {/* Review Mistakes */}
          {incorrectCount > 0 && (
            <Card className="p-4 flex flex-col justify-between border border-gray-300 rounded-lg">
              <div>
                <div className="text-base font-semibold text-black mb-1">
                  Review Mistakes
                </div>
                <div className="text-sm text-gray-600 mb-4">
                  Review the questions you got wrong to improve your knowledge.
                </div>
              </div>
              <Button
                className={`w-full border-2 border-red-600 ${
                  selectedButton === "review"
                    ? "!bg-red-600 !text-white"
                    : "!bg-white !text-red-600"
                }`}
                onClick={handleReviewMistakes}
              >
                Review Mistakes
              </Button>
            </Card>
          )}
          {/* Next Lesson */}
          <Card className="p-4 flex flex-col justify-between border border-gray-300 rounded-lg">
            <div>
              <div className="text-base font-semibold text-black mb-1">
                Return to Lessons
              </div>
              <div className="text-sm text-gray-600 mb-4">
                Return to your lessons and continue when you are ready.
              </div>
            </div>
            <Button
              className={`w-full border-2 border-red-600 ${
                selectedButton === "next"
                  ? "!bg-red-600 !text-white"
                  : "!bg-white !text-red-600"
              }`}
              onClick={handleStartNextLesson}
            >
              Back To Lessons
            </Button>
          </Card>
        </div>

      </div>

      {/* Chapter Not Passed modal. Appears once all required chapter questions have been
          answered and the cumulative chapter score is still below the passing mark. */}
      {showNotPassedModal && (
        <div
          className="fixed inset-0 z-[102] flex items-center justify-center px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="chapter-not-passed-title"
        >
          <div className="absolute inset-0 bg-black bg-opacity-40" />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 sm:p-8 text-left">
            <div className="w-10 h-10 rounded-full bg-[#FFF7E8] flex items-center justify-center mb-4">
              <AlertTriangle size={20} className="text-[#B54708]" />
            </div>
            <h3
              id="chapter-not-passed-title"
              className="text-lg sm:text-xl font-bold text-black mb-2"
            >
              Chapter Not Passed
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              You completed this chapter, but your score is below the required{" "}
              {passingScore}%. Review your answers and retry the chapter. You
              will unlock the next chapter once you score {passingScore}% or
              higher.
            </p>
            <div className="bg-[#FFF7E8] border border-[#F4C56A] rounded-lg px-4 py-3 mb-5">
              <span className="text-sm font-semibold text-[#B54708]">
                Your chapter score: {chapterScore}% • Passing score:{" "}
                {passingScore}%
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
              <Button
                className="w-full border-2 border-[#A5003B] !bg-white !text-[#A5003B] hover:!bg-[#FFF1F3]"
                onClick={handleReviewMistakes}
              >
                Review Mistakes
              </Button>
              <Button
                className="w-full border-2 border-[#A5003B] !bg-[#A5003B] !text-white hover:!bg-[#7d0030]"
                onClick={handleRetryChapter}
              >
                Retry Chapter
              </Button>
            </div>
            <Button
              className="w-full border-2 border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-50"
              onClick={handleStartNextLesson}
            >
              Return to Lessons
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonCompletePanel;
